import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import type { ErrorDescription } from "@/lib/errors/describe";
import type { ToastStore } from "@/lib/toast";

/**
 * `meta` flags a query/mutation can set:
 * - `silent: true` — never toast (shell badges, optional lookups)
 * - queries: `errorToast: false` — the screen shows the error itself, even on background refetches
 * - mutations: `errorToast: true` — opt in to the standard error toast (most screens show the
 *   failure next to the button that failed, e.g. `mutation.isError`)
 */
declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { silent?: boolean; errorToast?: boolean };
    mutationMeta: { silent?: boolean; errorToast?: boolean };
  }
}

export interface QueryFeedback {
  toasts: ToastStore;
  describe: (error: unknown) => ErrorDescription;
}

function report(feedback: QueryFeedback | undefined, error: unknown) {
  if (!feedback) {
    return;
  }
  const described = feedback.describe(error);
  if (described.status === 401) {
    return; // the auth layer refreshes / shows "sign in again"
  }
  feedback.toasts.error(described.message, {
    hint: described.hint ?? undefined,
    requestId: described.requestId,
    dedupeKey: `${described.code}:${described.status}`,
  });
}

/**
 * Back-office defaults: stock/orders change often, so nothing is fresh for long (realtime updates
 * arrive through `@tcg/realtime` — T11-52). Error feedback (T11-06): the first load of a
 * screen shows its own error state; a *background* refetch that fails (data already on screen)
 * raises an error toast with the reference code, as does a mutation that opts in.
 */
export function createQueryClient(feedback?: QueryFeedback): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.silent || query.meta?.errorToast === false) {
          return;
        }
        if (query.state.data !== undefined) {
          report(feedback, error);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (
          mutation.meta?.silent ||
          mutation.meta?.errorToast !== true ||
          mutation.options.onError
        ) {
          return;
        }
        report(feedback, error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: (failureCount, error) => {
          const status = (error as { status?: unknown }).status;
          // client errors do not get better by retrying
          if (typeof status === "number" && status >= 400 && status < 500) {
            return false;
          }
          return failureCount < 1;
        },
        refetchOnWindowFocus: true,
      },
      mutations: { retry: 0 },
    },
  });
}
